import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useT } from '../../i18n/LangProvider';
import { listAdmins, saveAdmin, deleteAdmin, getUser } from '../../lib/api';
import type { AdminRecord } from '../../lib/api';

export default function AdminEditors() {
  const { t } = useT();
  const [admins, setAdmins] = useState<AdminRecord[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'superadmin'>('admin');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, user] = await Promise.all([listAdmins(), getUser()]);
      setAdmins(data.admins);
      setMe(user?.userDetails?.toLowerCase() ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const myRecord = admins.find((a) => a.email === me);
  const iAmSuper = myRecord?.role === 'superadmin';

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.includes('@')) return;
    setBusy(true);
    setError(null);
    try {
      const data = await saveAdmin(newEmail.trim().toLowerCase(), newRole);
      setAdmins(data.admins);
      setNewEmail('');
      setNewRole('admin');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const removeAdmin = async (email: string) => {
    if (!confirm(`Remove ${email} as admin?`)) return;
    setBusy(true);
    setError(null);
    try {
      const data = await deleteAdmin(email);
      setAdmins(data.admins);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const updateRole = async (email: string, role: 'admin' | 'superadmin') => {
    setBusy(true);
    setError(null);
    try {
      const data = await saveAdmin(email, role);
      setAdmins(data.admins);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <Link to="/admin" className="text-xs text-ink-muted hover:text-ink lowercase">{t('admin.back')}</Link>

      <p className="text-xs tracking-[0.25em] uppercase text-brand-red mt-5 mb-3">{t('admin.ed.kicker')}</p>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight">{t('admin.ed.title')}</h1>
      <p className="text-ink-muted mt-5 max-w-2xl">
        {t('admin.ed.lead')}
      </p>

      {error && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 rounded-xl bg-red-950/50 border border-red-700/50 text-red-300 text-sm">
          {error}
        </motion.div>
      )}

      {/* Add form */}
      <form onSubmit={add} className="mt-10 bg-bg-elev rounded-2xl p-6 border border-white/[0.07]">
        <p className="text-xs text-ink-muted uppercase tracking-wider mb-4">{t('admin.ed.addAdmin')}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="colleague@tecsys.com"
            className="flex-1 rounded-xl border border-white/[0.10] bg-bg-base p-3 text-ink text-sm focus:border-brand-red outline-none"
            required
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as 'admin' | 'superadmin')}
            className="rounded-xl border border-white/[0.10] bg-bg-base p-3 text-ink text-sm focus:border-brand-red outline-none"
            disabled={!iAmSuper}
          >
            <option value="admin">admin</option>
            <option value="superadmin" disabled={!iAmSuper}>superadmin {iAmSuper ? '' : '(superadmin only)'}</option>
          </select>
          <button
            type="submit"
            disabled={busy || !newEmail.includes('@')}
            className="bg-brand-red text-white text-sm rounded-full px-6 py-3 disabled:opacity-40 hover:bg-brand-red-soft transition-colors whitespace-nowrap"
          >
            {busy ? t('admin.ed.adding') : t('admin.ed.addAdmin')}
          </button>
        </div>
        <p className="text-xs text-ink-faint mt-3">
          {t('admin.ed.hint')}
        </p>
      </form>

      {/* List */}
      <section className="mt-10">
        <p className="text-xs text-ink-muted uppercase tracking-wider mb-4">{admins.length} {t('admin.ed.count')}</p>
        {loading ? (
          <p className="text-ink-muted">{t('admin.ed.loading')}</p>
        ) : (
          <div className="space-y-2">
            <AnimatePresence>
              {admins.map((a) => (
                <motion.div
                  key={a.email}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex items-center justify-between gap-4 p-4 bg-bg-elev rounded-xl border border-white/[0.07]"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                      a.role === 'superadmin' ? 'bg-brand-red' : 'bg-ink'
                    }`}>
                      {a.email[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-ink font-medium truncate flex items-center gap-2">
                        {a.email}
                        {a.email === me && <span className="text-[10px] text-ink-muted border border-white/[0.12] rounded px-1.5 py-0.5">you</span>}
                      </div>
                      <div className="text-xs text-ink-muted">
                        {a.role === 'superadmin' ? 'super-admin' : 'admin'}
                        {a.addedBy && a.addedBy !== 'bootstrap' && ` · added by ${a.addedBy}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {iAmSuper && a.email !== me && (
                      <select
                        value={a.role}
                        onChange={(e) => updateRole(a.email, e.target.value as 'admin' | 'superadmin')}
                        disabled={busy}
                        className="text-xs rounded-full border border-white/[0.10] bg-bg-elev px-3 py-1.5 outline-none focus:border-brand-red"
                      >
                        <option value="admin">admin</option>
                        <option value="superadmin">superadmin</option>
                      </select>
                    )}
                    {a.email !== me && (
                      <button
                        onClick={() => removeAdmin(a.email)}
                        disabled={busy}
                        className="text-xs px-3 py-1.5 rounded-full border border-white/[0.10] hover:border-brand-red hover:text-brand-red transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </section>
    </div>
  );
}
